import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSatelliteStore } from '../../store/satelliteStore';
import { getGeographicPosition } from '../../store/satelliteStore';
import type { Satellite } from '../../store/satelliteStore';
import { FaMusic, FaPause, FaSatellite } from 'react-icons/fa';
import { audioService } from '../../services/audioService';

const categories = [
  { 
    id: 'active', 
    name: 'Active Satellites', 
    icon: '🛰️',
    color: '#00F5FF',
    displayName: 'Active Satellites'
  },
  { 
    id: 'stations', 
    name: 'Space Stations', 
    icon: '🚀',
    color: '#4F46E5',
    displayName: 'Space Station'
  },
  { 
    id: 'debris', 
    name: 'Space Debris', 
    icon: '⚠️',
    color: '#FF0000',
    displayName: 'Debris'
  },
  { 
    id: 'starlink', 
    name: 'Starlink', 
    icon: '📡',
    color: '#FFD700',
    displayName: 'Starlink'
  },
];

const Sidebar = () => {
  const [activeCategories, setActiveCategories] = useState<string[]>(['active', 'starlink']);
  const [isPlaying, setIsPlaying] = useState(false);
  const { 
    satellites, 
    loading, 
    error, 
    selectedSatellite, 
    setSelectedSatellite, 
    fetchSatellites,
    objectLimit,
    setObjectLimit 
  } = useSatelliteStore();

  useEffect(() => {
    console.log('Fetching satellites for categories:', activeCategories);
    // Map the UI categories to the API categories
    const apiCategories = activeCategories.map(cat => {
      switch(cat) {
        case 'active': return 'active';
        case 'stations': return 'stations';
        case 'debris': return 'debris';
        case 'starlink': return 'starlink';
        default: return '';
      }
    }).filter(Boolean);
    
    fetchSatellites(apiCategories);
  }, [activeCategories, fetchSatellites]);

  useEffect(() => {
    console.log('Current satellites:', satellites);
  }, [satellites]);

  // Initialize audio on component mount
  useEffect(() => {
    const initAudio = async () => {
      const autoplaySuccessful = await audioService.init();
      setIsPlaying(autoplaySuccessful);
    };
    
    initAudio();
    
    // Cleanup on unmount
    return () => {
      audioService.pause();
    };
  }, []);

  const toggleCategory = (categoryId: string) => {
    setActiveCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleMusic = async () => {
    if (isPlaying) {
      audioService.pause();
    } else {
      await audioService.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSatelliteClick = (satelliteId: string | undefined) => {
    if (!satelliteId) return;
    
    // Toggle selection
    setSelectedSatellite(selectedSatellite === satelliteId ? null : satelliteId);
  };

  // Add hover handlers
  const handleSatelliteHover = (satelliteId: string | undefined) => {
    if (!satelliteId) return;
    useSatelliteStore.getState().setHoveredSatellite(satelliteId);
  };

  const handleSatelliteUnhover = () => {
    useSatelliteStore.getState().setHoveredSatellite(null);
  };

  const visibleSatellites = useMemo(() => {
    // First, categorize all satellites
    const categorized = satellites.reduce<Record<string, Satellite[]>>((acc, sat) => {
      const category = sat.type === 'station' ? 'stations' : sat.type;
      if (!acc[category]) acc[category] = [];
      acc[category].push(sat);
      return acc;
    }, {});

    // Filter active categories that have satellites
    const activeCats = activeCategories.filter(category => 
      categorized[category] && categorized[category].length > 0
    );

    if (activeCats.length === 0) return [];

    // Calculate base number of objects per category
    let remainingLimit = objectLimit;
    const result: Satellite[] = [];

    // First pass: ensure each category gets at least one object
    activeCats.forEach(category => {
      if (categorized[category].length > 0) {
        result.push(categorized[category][0]);
        remainingLimit--;
      }
    });

    // Second pass: distribute remaining objects evenly
    while (remainingLimit > 0 && result.length < objectLimit) {
      const basePerCategory = Math.floor(remainingLimit / activeCats.length);
      let distributed = false;

      for (const category of activeCats) {
        const currentCategoryCount = result.filter(sat => 
          (sat.type === 'station' ? 'stations' : sat.type) === category
        ).length;
        
        const availableInCategory = categorized[category].length - currentCategoryCount;
        if (availableInCategory > 0) {
          const toAdd = Math.min(basePerCategory, availableInCategory, remainingLimit);
          if (toAdd > 0) {
            result.push(...categorized[category].slice(currentCategoryCount, currentCategoryCount + toAdd));
            remainingLimit -= toAdd;
            distributed = true;
          }
        }
      }

      // Break if we couldn't distribute any more satellites
      if (!distributed) break;
    }

    return result;
  }, [satellites, activeCategories, objectLimit]);

  // Group visible satellites by type for display
  const groupedVisibleSatellites = useMemo(() => {
    return visibleSatellites.reduce<Record<string, Satellite[]>>((acc, sat) => {
      // Map station type to stations category
      const category = sat.type === 'station' ? 'stations' : sat.type;
      if (!acc[category]) acc[category] = [];
      acc[category].push(sat);
      return acc;
    }, {});
  }, [visibleSatellites]);

  return (
    <>
      <motion.div 
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="w-80 absolute left-8 top-8 z-10 bg-[#0B1026]/30 backdrop-blur-2xl rounded-2xl border border-indigo-500/20 overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-b from-indigo-500/10 to-transparent border-b border-indigo-500/20">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-500 via-[#00F5FF] to-purple-500 bg-clip-text text-transparent">
            Space Tracker
          </h1>
        </div>

        {/* Object Limit Slider */}
        <div className="p-4 border-b border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <FaSatellite className="text-indigo-400" />
            <span className="text-sm text-gray-300">Object Limit: {objectLimit}</span>
          </div>
          <input
            type="range"
            min="0"
            max="25000"
            value={objectLimit}
            onChange={(e) => setObjectLimit(Number(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Categories - Now Vertical */}
        <div className="flex flex-col p-4 gap-2 border-b border-indigo-500/20">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => toggleCategory(category.id)}
              className={`px-4 py-3 rounded-lg transition-all duration-300 relative overflow-hidden flex items-center ${
                activeCategories.includes(category.id)
                  ? 'bg-gradient-to-r from-indigo-500/20 to-[#00F5FF]/20 border border-[#00F5FF]/50 text-[#00F5FF] shadow-[0_0_15px_rgba(0,245,255,0.1)]'
                  : 'hover:bg-indigo-500/10 border border-transparent hover:border-indigo-500/30 text-indigo-200/80 hover:shadow-[0_0_15px_rgba(0,245,255,0.1)]'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-[#00F5FF]/10 to-indigo-500/0 animate-shine" />
              <span className="mr-3 text-xl">{category.icon}</span>
              <span className="font-medium">{category.name}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 max-h-[calc(100vh-24rem)] overflow-y-auto custom-scrollbar">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#00F5FF] uppercase tracking-wider flex items-center">
                <span className="w-2 h-2 bg-[#00F5FF] rounded-full mr-2 animate-pulse" />
                {activeCategories.length > 0 ? 'Active Objects' : 'No Selection'}
              </h2>
              <span className="text-indigo-200/60 text-sm">
                {visibleSatellites.length} / {satellites.length}
              </span>
            </div>

            {loading && (
              <div className="flex items-center justify-center p-8">
                <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-[#00F5FF] rounded-full animate-spin" />
              </div>
            )}
            
            {error && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
                {error}
              </div>
            )}

            {/* Category headers with counts */}
            {Object.entries(groupedVisibleSatellites).map(([type, sats]) => (
              <div key={type} className="space-y-2">
                <div className="flex items-center justify-between text-sm text-indigo-200/60 px-2">
                  <span>{categories.find(c => c.id === type)?.displayName}</span>
                  <span>{sats.length} objects</span>
                </div>
                {sats.map((satellite: Satellite) => {
                  // Map station type to stations for color lookup
                  const displayType = satellite.type === 'station' ? 'stations' : satellite.type;
                  const categoryColor = categories.find(c => c.id === displayType)?.color || '#00F5FF';
                  const displayName = categories.find(c => c.id === displayType)?.displayName || 'Unknown';
                  
                  return (
                    <motion.div
                      key={satellite.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`p-4 rounded-lg backdrop-blur-sm transition-all duration-300 cursor-pointer group ${
                        selectedSatellite === satellite.id 
                          ? 'bg-gradient-to-r from-indigo-500/10 to-[#00F5FF]/10 border-2 shadow-lg' 
                          : 'hover:bg-indigo-500/10 border-2 border-transparent hover:border-indigo-500/30'
                      }`}
                      onClick={() => handleSatelliteClick(satellite.id)}
                      onMouseEnter={() => handleSatelliteHover(satellite.id)}
                      onMouseLeave={handleSatelliteUnhover}
                      style={{
                        borderColor: selectedSatellite === satellite.id 
                          ? categoryColor + '80'
                          : 'transparent',
                        backgroundColor: selectedSatellite === satellite.id 
                          ? `${categoryColor}10`
                          : 'transparent'
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <h3 className="text-lg font-semibold bg-gradient-to-r from-white to-indigo-200 bg-clip-text text-transparent group-hover:from-[#00F5FF] group-hover:to-indigo-300">
                          {satellite.name}
                        </h3>
                        <span 
                          className="px-2 py-1 rounded-full text-xs"
                          style={{
                            backgroundColor: `${categoryColor}20`,
                            color: categoryColor
                          }}
                        >
                          {displayName}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        <p className="text-indigo-200/80 text-sm">
                          Position: {getGeographicPosition(satellite.position).lat.toFixed(2)}°N, {getGeographicPosition(satellite.position).lng.toFixed(2)}°E
                        </p>
                        <p className="text-indigo-200/80 text-sm">
                          Altitude: {getGeographicPosition(satellite.position).alt.toFixed(2)} km
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Live View Badge - Bottom Right */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-8 right-8 z-10 bg-[#0B1026]/30 backdrop-blur-2xl rounded-xl border border-indigo-500/20 p-3 flex items-center space-x-3"
      >
        <span className="w-2 h-2 bg-[#00F5FF] rounded-full animate-pulse" />
        <span className="text-[#00F5FF] font-medium">Live View</span>
      </motion.div>

      {/* Music Control - Top Right */}
      <motion.button
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onClick={toggleMusic}
        className="fixed top-8 right-8 z-50 bg-[#0B1026]/30 backdrop-blur-2xl rounded-xl border border-indigo-500/20 p-3 hover:bg-indigo-500/10 transition-all duration-300"
      >
        {isPlaying ? (
          <FaPause className="text-[#00F5FF] w-5 h-5" />
        ) : (
          <FaMusic className="text-[#00F5FF] w-5 h-5" />
        )}
      </motion.button>
    </>
  );
};

export default Sidebar; 